<!-- 贸易AI助手：选择目的国/推算范围/是否保存 + 上传Excel或输入文字，一次请求返回全部票，按票页签展示申报建议表格 -->
<template>
    <div class="gw-ai-wrap">
        <!-- 顶部标题 -->
        <div class="gw-ai-header">
            <div class="gw-ai-title">
                <span class="gw-ai-dot"></span>
                <span>贸易AI助手</span>
            </div>
            <span class="gw-ai-sub">选择目的国后上传Excel或输入文字，AI将返回申报建议</span>
        </div>
        <!-- 消息区 -->
        <div class="gw-ai-body" ref="msgBody">
            <!-- 空状态 -->
            <div class="gw-ai-empty" v-if="messages.length === 0">
                <i class="el-icon-chat-dot-round"></i>
                <p>请选择目的国与推算范围，再上传 Excel 或输入文字描述</p>
                <span>AI 将以表格方式返回商品申报建议</span>
            </div>
            <div v-for="(msg, index) in messages" :key="index">
                <!-- 用户消息 -->
                <div class="gw-msg-row gw-msg-user" v-if="msg.role === 'user'">
                    <div class="gw-avatar gw-avatar-user">我</div>
                    <div class="gw-bubble gw-bubble-user">
                        <span class="gw-pill"><i class="el-icon-location-outline"></i>目的国：{{ msg.country }}</span>
                        <span class="gw-pill" v-if="msg.mode === 'excel'"><i class="el-icon-document"></i>{{ msg.fileName }}</span>
                        <span class="gw-pill gw-pill-weak" v-if="msg.mode === 'excel'">范围：{{ msg.scope || '进出口' }}　保存：{{ msg.save ? '是' : '否' }}　会话：{{ msg.session ? '是' : '否' }}</span>
                        <div class="gw-user-text" v-else>{{ msg.content }}</div>
                    </div>
                </div>
                <!-- AI消息 -->
                <div class="gw-msg-row" v-else>
                    <div class="gw-avatar">AI</div>
                    <div class="gw-bubble gw-bubble-ai">
                        <!-- 解析中 -->
                        <div class="gw-ai-loading" v-if="msg.loading">
                            <i class="el-icon-loading"></i><span>{{ msg.text }}</span>
                        </div>
                        <!-- 失败/提示 -->
                        <div class="gw-ai-error" v-else-if="msg.error">
                            <i class="el-icon-warning-outline"></i><span>{{ msg.text }}</span>
                        </div>
                        <!-- 结果：按票（页签）展示表格 -->
                        <template v-else>
                            <div class="gw-ai-text-row">
                                <p class="gw-ai-text">{{ resultIntro(msg) }}</p>
                                <el-button size="mini" plain icon="el-icon-download" :loading="exporting" @click="exportExcel(msg)">导出Excel</el-button>
                            </div>
                            <!-- 归档结果：保存用户数据后后端生成的批次号（数组） -->
                            <div v-if="msg.archiveSaved" class="gw-archive-tip">
                                <i class="el-icon-circle-check gw-archive-icon"></i>
                                <div class="gw-archive-body">
                                    <div class="gw-archive-head">
                                        <span class="gw-archive-title">已生成以下归档批次</span>
                                        <span class="gw-archive-count" v-if="msg.archiveBatchNos && msg.archiveBatchNos.length">共 {{ msg.archiveBatchNos.length }} 个</span>
                                    </div>
                                    <div class="gw-archive-list" v-if="msg.archiveBatchNos && msg.archiveBatchNos.length">
                                        <span class="gw-archive-chip" v-for="(no, index) in msg.archiveBatchNos" :key="index">{{ no }}</span>
                                    </div>
                                    <div class="gw-archive-empty" v-else>接口未返回批次号</div>
                                </div>
                            </div>
                            <div v-if="msg.archiveDuplicate" class="gw-archive-tip">
                                <div class="gw-archive-empty">归档记录已存在</div>
                            </div>
                            <!-- 接口级提示，如页签序号回退/重复、缺税号等 -->
                            <el-alert v-if="msg.warnings && msg.warnings.length" class="gw-ai-alert" type="warning"
                                :closable="false" show-icon :title="warningsText(msg.warnings)"></el-alert>
                            <el-tabs v-model="msg.activeTab" class="gw-ai-tabs">
                                <el-tab-pane v-for="ticket in msg.tickets" :key="ticket.key" :name="ticket.key"
                                    :label="tabLabel(ticket)">
                                    <el-table :data="ticket.rows" border size="mini" class="gw-ai-table" :row-class-name="rowClassName">
                                        <el-table-column prop="rowNo" label="序号" width="55" align="center" :formatter="fmt"></el-table-column>
                                        <el-table-column prop="name" label="品名" width="110" show-overflow-tooltip :formatter="fmt"></el-table-column>
                                        <!-- 出口侧列：推算范围为「只进口」且无出口数据时不展示 -->
                                        <el-table-column prop="hsCode" label="出口HS编码" width="105" align="center" v-if="ticket.showExport" :formatter="fmt"></el-table-column>
                                        <el-table-column label="出口申报要素" width="240" v-if="ticket.showExport">
                                            <template slot-scope="scope">
                                                <div class="gw-cell-long" :title="cellText(scope.row, 'cnElement')">{{ cellText(scope.row, 'cnElement') }}</div>
                                            </template>
                                        </el-table-column>
                                        <el-table-column prop="exportSource" label="出口来源" width="85" align="center" v-if="ticket.showExport" :formatter="fmt"></el-table-column>
                                        <el-table-column label="出口来源备注" width="150" v-if="ticket.showExport">
                                            <template slot-scope="scope">
                                                <div class="gw-cell-long" :title="cellText(scope.row, 'exportSourceNote')">{{ cellText(scope.row, 'exportSourceNote') }}</div>
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="出口缺项提示" width="150" v-if="ticket.showExport">
                                            <template slot-scope="scope">
                                                <div class="gw-cell-long gw-cell-warn" :title="cellText(scope.row, 'elementMissing')">{{ cellText(scope.row, 'elementMissing') }}</div>
                                            </template>
                                        </el-table-column>
                                        <el-table-column prop="dualUseRisk" label="双用途风险" width="85" align="center" v-if="ticket.showExport" :formatter="fmt"></el-table-column>
                                        <!-- 进口侧列：推算范围为「只出口」且无进口数据时不展示 -->
                                        <el-table-column prop="importHsCode" label="进口HS编码" width="105" align="center" v-if="ticket.showImport" :formatter="fmt"></el-table-column>
                                        <el-table-column prop="engName" label="进口英文名" width="130" show-overflow-tooltip v-if="ticket.showImport" :formatter="fmt"></el-table-column>
                                        <el-table-column label="进口申报要素" width="240" v-if="ticket.showImport">
                                            <template slot-scope="scope">
                                                <div class="gw-cell-long" :title="cellText(scope.row, 'importElement')">{{ cellText(scope.row, 'importElement') }}</div>
                                            </template>
                                        </el-table-column>
                                        <el-table-column prop="importSource" label="进口来源" width="85" align="center" v-if="ticket.showImport" :formatter="fmt"></el-table-column>
                                        <el-table-column label="进口来源备注" width="150" v-if="ticket.showImport">
                                            <template slot-scope="scope">
                                                <div class="gw-cell-long" :title="cellText(scope.row, 'importSourceNote')">{{ cellText(scope.row, 'importSourceNote') }}</div>
                                            </template>
                                        </el-table-column>
                                        <!-- 行级风险提示 -->
                                        <el-table-column label="风险提示" width="170">
                                            <template slot-scope="scope">
                                                <div class="gw-cell-long gw-cell-warn" :title="warnText(scope.row)">{{ warnText(scope.row) }}</div>
                                            </template>
                                        </el-table-column>
                                        <!-- 是否保存=是时，展示每行出口/进口的入库条件 -->
                                        <el-table-column label="可入库" width="90" align="center" v-if="msg.save">
                                            <template slot-scope="scope">
                                                <div class="gw-save-flags">
                                                    <span v-if="ticket.showExport" class="gw-save-flag" :class="scope.row.exportSavable ? 'gw-save-yes' : 'gw-save-no'">出口{{ scope.row.exportSavable ? '可' : '不可' }}入库</span>
                                                    <span v-if="ticket.showImport" class="gw-save-flag" :class="scope.row.importSavable ? 'gw-save-yes' : 'gw-save-no'">进口{{ scope.row.importSavable ? '可' : '不可' }}入库</span>
                                                </div>
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="状态" width="75" align="center">
                                            <template slot-scope="scope">
                                                <el-tooltip v-if="scope.row.elementMissing" :content="scope.row.elementMissing" placement="top">
                                                    <el-tag type="warning" size="mini">缺要素</el-tag>
                                                </el-tooltip>
                                                <el-tag v-else type="success" size="mini">正常</el-tag>
                                            </template>
                                        </el-table-column>
                                    </el-table>
                                </el-tab-pane>
                            </el-tabs>
                            <!-- 补要素：行数据 missingNames 非空时展示，全部填写后提交到 tradeai/session/update -->
                            <div class="gw-missing-card" v-if="msg.missingRows && msg.missingRows.length">
                                <div class="gw-missing-head">
                                    <i class="el-icon-edit-outline"></i>
                                    <span>以下商品缺少申报要素，请补充后提交</span>
                                </div>
                                <div class="gw-missing-row" v-for="row in msg.missingRows" :key="'miss-' + row.rowNo">
                                    <span class="gw-missing-label">第 {{ row.rowNo }} 行「{{ row.name }}」缺失：</span>
                                    <div class="gw-missing-fields">
                                        <div class="gw-missing-field" v-for="name in row.names" :key="name">
                                            <label>{{ name }}</label>
                                            <el-input v-model="msg.missingInputs[row.rowNo + '|' + name]" size="mini"
                                                :disabled="msg.updating" :placeholder="'请输入' + name"></el-input>
                                        </div>
                                    </div>
                                </div>
                                <div class="gw-missing-foot">
                                    <span class="gw-missing-tip">全部填写完成后方可提交，提交后表格按最新结果刷新</span>
                                    <el-button type="primary" size="mini" icon="el-icon-s-promotion"
                                        :loading="msg.updating" @click="submitUpdates(msg)">提交补充</el-button>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <!-- 底部操作区：第一行解析设置，第二行文字输入 -->
        <div class="gw-ai-footer">
            <div class="gw-compose-toolbar">
                <el-select v-model="country" filterable size="small" placeholder="目的国(地区)" class="gw-country-select" :disabled="sending"
                remote :remote-method="getCountryList">
                    <el-option v-for="item in countryList" :key="item.codeValue + '-' + item.cusCode" :label="item.codeName" :value="item.codeName">
                        <span class="gw-opt-code">{{ item.codeValue }}</span>
                        <span class="gw-opt-name">{{ item.codeName }}</span>
                        <span>{{ item.cusCode }}</span>
                    </el-option>
                </el-select>
                <span class="gw-tool-divider"></span>
                <!-- 是否保存：对应接口 save，是=true 否=false -->
                <div class="gw-tool-item">
                    <span class="gw-tool-label">是否保存</span>
                    <el-select v-model="saveValue" size="small" class="gw-mini-select" :disabled="sending">
                        <el-option label="是" :value="true"></el-option>
                        <el-option label="否" :value="false"></el-option>
                    </el-select>
                </div>
                <!-- 推算范围：对应接口 scope，进出口传空 -->
                <div class="gw-tool-item">
                    <span class="gw-tool-label">推算范围</span>
                    <el-select v-model="scopeValue" size="small" class="gw-mini-select gw-scope-select" :disabled="sending">
                        <el-option label="只出口" value="只出口"></el-option>
                        <el-option label="只进口" value="只进口"></el-option>
                        <el-option label="进出口" value=""></el-option>
                    </el-select>
                </div>
                <!-- 连续会话：对应接口 session，仅对Excel接口生效 -->
                <div class="gw-tool-item">
                    <span class="gw-tool-label">连续会话</span>
                    <el-select v-model="sessionValue" size="small" class="gw-mini-select" :disabled="sending">
                        <el-option label="是" :value="true"></el-option>
                        <el-option label="否" :value="false"></el-option>
                    </el-select>
                </div>
                <el-upload ref="excelUpload" action="#" accept=".xlsx,.xls" :auto-upload="false" :show-file-list="false"
                    :on-change="onFileChange" :disabled="sending">
                    <el-button size="small" icon="el-icon-upload2" class="gw-upload-btn" :disabled="sending">上传Excel</el-button>
                </el-upload>
                <!-- 已选文件 -->
                <div class="gw-file-chip" v-if="file">
                    <i class="el-icon-document"></i>
                    <span class="gw-file-name">{{ file.name }}</span>
                    <i class="el-icon-close" v-if="!sending" @click="removeFile"></i>
                </div>
                <span class="gw-compose-hint">
                    <i class="el-icon-info"></i>
                    <template v-if="file">发送时优先按 Excel 解析，如需按文字识别请先移除文件</template>
                    <template v-else>推算范围、是否保存、连续会话仅对 Excel 生效</template>
                </span>
            </div>
            <!-- 文字输入：整体一个带边框的输入框，发送按钮固定在右下角 -->
            <div class="gw-compose-box" :class="{ 'gw-compose-focus': inputFocused }">
                <el-input v-model="inputText" type="textarea" size="small" :autosize="{ minRows: 2, maxRows: 4 }"
                    class="gw-text-input" :disabled="sending"
                    placeholder="输入商品描述，例如：出口电动机，功率4KW，品牌西门子"
                    @focus="inputFocused = true" @blur="inputFocused = false"
                    @keydown.native.ctrl.enter.prevent="sendMessage"></el-input>
                <div class="gw-compose-actions">
                    <span class="gw-compose-kbd">Ctrl + Enter 发送</span>
                    <el-button type="primary" size="small" icon="el-icon-s-promotion" :loading="sending"
                        class="gw-send-btn" @click="sendMessage">发送</el-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ExcelJS from 'exceljs'
import AEObasicData from '@/config/AEObasicData.js'
// 连续会话ID在 sessionStorage 中的键：页面刷新/重开时用它恢复会话
const SESSION_KEY = 'gwAiAssistantSessionId'
export default {
    name: 'gwAIAssistant',
    created() {
        // 页面打开/刷新时，若有会话记录则恢复上次会话数据
        this.restoreSession()
    },
    data() {
        return {
            // 目的国数据源：与出口报关单表体「最终目的国」一致
            countryList: AEObasicData.CUS_MAPPING_COUNTRY_CODE_V,
            // 选中的目的国（codeName 汉字）
            country: '',
            // 已选Excel文件 { name, raw }
            file: null,
            // 文字描述输入内容
            inputText: '',
            // 输入框是否聚焦（用于边框高亮）
            inputFocused: false,
            // 是否保存解析结果：接口 save，是=true，否=false（默认否）
            saveValue: false,
            // 推算范围：接口 scope，只出口/只进口，进出口传空字符串（默认进出口）
            scopeValue: '',
            // 连续会话：接口 session，仅对Excel接口生效，是=true，否=false（默认否）
            sessionValue: false,
            // 是否发送中
            sending: false,
            // 是否正在导出Excel
            exporting: false,
            // 消息列表
            // { role:'user', country, mode:'excel', fileName, scope, save } 用户消息（Excel）
            // { role:'user', country, mode:'text', content } 用户消息（文字）
            // { role:'ai', loading:true, text } 解析中
            // { role:'ai', error:true, text } 失败/提示
            // { role:'ai', mode, country, scope, save, sessionId, missingRows, missingInputs, updating, tickets:[{key,ticket,sheetName,rows,showExport,showImport}], totalTickets, totalRows, warnings, activeTab, archiveSaved, archiveDuplicate, archiveBatchNos } 建议结果（按票分页签）
            messages: [],
        }
    },
    watch: {
        // 只在新消息入列时滚动到底部；不要 deep，避免切换页签时被拽回底部
        'messages.length'() {
            this.scrollBottom()
        },
    },
    methods: {
        getCountryList(value){
            if(value && value !== ' '){
                this.countryList = AEObasicData.CUS_MAPPING_COUNTRY_CODE_V.filter(item =>
                    item.codeValue.toLowerCase().includes(value.toLowerCase()) ||
                    item.codeName.toLowerCase().includes(value.toLowerCase()) || 
                    item.cusCode.toLowerCase().includes(value.toLowerCase())
                );
            }
        },
        // 选择Excel文件
        onFileChange(file) {
            const name = file.name || ''
            if (!/\.(xlsx|xls)$/i.test(name)) {
                this.$message.error('仅支持上传 .xlsx / .xls 格式的Excel文件')
                this.$refs.excelUpload.clearFiles()
                return
            }
            this.file = { name: name, raw: file.raw }
        },
        // 移除已选文件
        removeFile() {
            this.file = null
            this.$refs.excelUpload.clearFiles()
        },
        // 发送：已选文件走Excel接口，否则走文字接口
        sendMessage() {
            if (this.sending) return
            if (!this.country) {
                this.$message.warning('请先选择目的国')
                return
            }
            const text = (this.inputText || '').trim()
            if (!this.file && !text) {
                this.$message.warning('请上传Excel文件或输入文字描述')
                return
            }
            if (this.file) {
                this.sendExcel(this.country, this.file)
            } else {
                this.sendText(this.country, text)
            }
        },
        // 上传Excel：只调一个接口（tradeai/suggest/excel），一次返回全部票（页签）
        sendExcel(country, file) {
            const scope = this.scopeValue
            const save = this.saveValue
            const session = this.sessionValue
            // 用户消息入列（记录本次的推算范围、是否保存与连续会话，便于回看）
            this.messages.push({ role: 'user', country: country, mode: 'excel', fileName: file.name, scope: scope, save: save, session: session })
            // AI解析中提示
            const loadingMsg = { role: 'ai', loading: true, text: '正在解析Excel并生成申报建议，请稍候...' }
            this.messages.push(loadingMsg)
            // 清空输入
            this.country = ''
            this.removeFile()
            this.sending = true
            // 上传Excel，解析与建议由后端一次返回；目的国走 query，文件走 form-data
            const formData = new FormData()
            formData.append('file', file.raw)
            // 目的国必填；scope 传中文范围（进出口传空）；save、session 传布尔值
            this.$api.tradeaiSuggestExcel({ country: country, scope: scope, save: save, session: session }, formData).then(res => {
                const tickets = this.buildTickets(res)
                if (!tickets.length) {
                    this.replaceMsg(loadingMsg, { role: 'ai', error: true, text: '未解析到有效数据，请检查Excel内容后重试' })
                    return
                }
                this.replaceMsg(loadingMsg, this.buildResultMsg(tickets, country, 'excel', res, { scope: scope, save: save }))
            }).catch(() => {
                this.replaceMsg(loadingMsg, { role: 'ai', error: true, text: '解析失败，请稍后重试' })
            }).finally(() => {
                this.sending = false
            })
        },
        // 文字描述：调 tradeai/suggest，目的国拼接到 text 中（如「目的国越南，出口电动机」）
        sendText(country, text) {
            this.messages.push({ role: 'user', country: country, mode: 'text', content: text })
            const loadingMsg = { role: 'ai', loading: true, text: '正在根据文字描述生成申报建议，请稍候...' }
            this.messages.push(loadingMsg)
            this.country = ''
            this.inputText = ''
            this.sending = true
            this.$api.tradeaiSuggest({ text: '目的国' + country + '，' + text }).then(res => {
                const rows = this.normalizeRows(res)
                if (!rows.length) {
                    this.replaceMsg(loadingMsg, { role: 'ai', error: true, text: '未识别到有效数据，请调整文字描述后重试' })
                    return
                }
                // 文字接口直接返回行数组，页面按单票单页签展示
                const tickets = [{ key: 'tab-0', ticket: '文字识别', sheetName: '文字识别', rows: rows }]
                this.replaceMsg(loadingMsg, this.buildResultMsg(tickets, country, 'text', null))
            }).catch(() => {
                this.replaceMsg(loadingMsg, { role: 'ai', error: true, text: '生成失败，请稍后重试' })
            }).finally(() => {
                this.sending = false
            })
        },
        // 组装AI结果消息：Excel 带票/行合计与接口提示，文字只有行数据
        buildResultMsg(tickets, country, mode, res, extra) {
            const data = res || {}
            const meta = extra || {}
            const totalRows = tickets.reduce((n, t) => n + ((t.rows && t.rows.length) || 0), 0)
            const msg = {
                role: 'ai',
                mode: mode,
                country: country,
                // 本次请求的推算范围（空=进出口）与是否保存
                scope: meta.scope || '',
                save: !!meta.save,
                tickets: tickets,
                totalTickets: mode === 'text' ? 1 : (data.totalTickets || tickets.length),
                totalRows: data.totalRows || totalRows,
                warnings: this.toTextArray(data.warnings),
                // 保存用户数据后的归档结果（批次号为数组）
                archiveSaved: !!data.archiveSaved,
                archiveDuplicate: !!data.archiveDuplicate,
                archiveBatchNos: this.toTextArray(data.archiveBatchNos),
                // 连续会话ID：补要素提交时回传给 tradeai/session/update
                sessionId: data.sessionId || '',
                // 缺申报要素的行（missingNames 非空），仅Excel结果提供补要素
                missingRows: mode === 'excel' ? this.collectMissingRows(tickets) : [],
                // 补要素输入内容：key 为 rowNo|要素名
                missingInputs: {},
                // 补要素提交中
                updating: false,
                activeTab: tickets[0].key,
            }
            // 预生成补要素输入框的绑定 key，保证 v-model 响应式
            msg.missingRows.forEach(row => {
                row.names.forEach(name => {
                    msg.missingInputs[row.rowNo + '|' + name] = ''
                })
            })
            // 接口返回了 sessionId 就存入 sessionStorage，新值替换旧值，供刷新后恢复会话
            if (data.sessionId) {
                sessionStorage.setItem(SESSION_KEY, data.sessionId)
            }
            // 先算好每个页签展示出口/进口哪一侧，模板里直接按标记控制列
            msg.tickets.forEach(ticket => {
                const flags = this.sideFlags(ticket.rows, msg.scope)
                ticket.showExport = flags.export
                ticket.showImport = flags.import
            })
            return msg
        },
        // 收集缺失申报要素的行：missingNames 非空数组的行（跨全部页签）
        collectMissingRows(tickets) {
            const result = []
            ;(tickets || []).forEach(ticket => {
                (ticket.rows || []).forEach(row => {
                    const names = this.toTextArray(row && row.missingNames)
                    if (names.length) {
                        result.push({ rowNo: row.rowNo, name: row.name, names: names })
                    }
                })
            })
            return result
        },
        // 提交补要素：全部填写完才能提交，成功后用返回结果刷新当前消息表格
        submitUpdates(msg) {
            if (msg.updating) return
            if (!msg.sessionId) {
                this.$message.warning('缺少会话ID，无法提交补要素')
                return
            }
            // 校验所有缺失要素都已填写
            for (const row of msg.missingRows) {
                for (const name of row.names) {
                    const val = (msg.missingInputs[row.rowNo + '|' + name] || '').trim()
                    if (!val) {
                        this.$message.warning('请填写第 ' + row.rowNo + ' 行「' + row.name + '」的「' + name + '」')
                        return
                    }
                }
            }
            msg.updating = true
            // 组装 updates：rowNo + { 要素名: 填写值 }
            const updates = msg.missingRows.map(row => {
                const fields = {}
                row.names.forEach(name => {
                    fields[name] = (msg.missingInputs[row.rowNo + '|' + name] || '').trim()
                })
                return { rowNo: row.rowNo, fields: fields }
            })
            this.$api.tradeaiSessionUpdate({ sessionId: msg.sessionId, updates: updates }).then(res => {
                // 返回结构：{ sessionId, changedRowNos, rows（会话内全量行，平铺不分票）, warnings }
                const data = res || {}
                const rows = this.normalizeRows(data.rows)
                if (!rows.length) {
                    this.$message.warning('返回数据为空，请稍后重试')
                    return
                }
                // 按 rowNo 把返回的行合并回各页签，保留原页签结构
                const byNo = {}
                rows.forEach(row => {
                    if (row && row.rowNo !== null && row.rowNo !== undefined) byNo[row.rowNo] = row
                })
                msg.tickets.forEach(ticket => {
                    ticket.rows = (ticket.rows || []).map(row => {
                        const newRow = row && byNo[row.rowNo]
                        return newRow ? Object.assign({}, row, newRow) : row
                    })
                    // 行数据已变，重新计算页签两侧展示标记
                    const flags = this.sideFlags(ticket.rows, msg.scope)
                    ticket.showExport = flags.export
                    ticket.showImport = flags.import
                })
                // 刷新合计、接口提示、会话ID与缺失行状态
                msg.totalRows = msg.tickets.reduce((n, t) => n + ((t.rows && t.rows.length) || 0), 0)
                msg.warnings = this.toTextArray(data.warnings)
                msg.sessionId = data.sessionId || msg.sessionId
                // 有新sessionId则替换sessionStorage中的旧值
                if (data.sessionId) {
                    sessionStorage.setItem(SESSION_KEY, data.sessionId)
                }
                msg.missingRows = this.collectMissingRows(msg.tickets)
                msg.missingInputs = {}
                msg.missingRows.forEach(row => {
                    row.names.forEach(name => {
                        msg.missingInputs[row.rowNo + '|' + name] = ''
                    })
                })
                // 缺项全部补齐后卡片自动消失；提示本次实际更新的行号
                const changed = this.toTextArray(data.changedRowNos)
                this.$message.success(changed.length ? '补要素提交成功，已更新第 ' + changed.join('、') + ' 行' : '补要素提交成功')
            }).catch(() => {}).finally(() => {
                msg.updating = false
            })
        },
        // 页面打开/刷新时恢复上次会话：sessionStorage 有 sessionId 则调 GET /tradeai/session
        restoreSession() {
            const sessionId = sessionStorage.getItem(SESSION_KEY)
            if (!sessionId) return
            this.$api.tradeaiSessionGet(sessionId).then(res => {
                const data = res || {}
                const rows = this.normalizeRows(data.rows)
                if (!rows.length) return
                // 会话行是平铺结构，按单页签展示；行内 missingNames 非空时补要素卡片照常可用
                const tickets = [{ key: 'tab-0', ticket: '会话', sheetName: '历史会话', rows: rows }]
                const msg = this.buildResultMsg(tickets, data.country || '', 'excel', {
                    sessionId: data.sessionId || sessionId,
                    totalRows: rows.length,
                    warnings: data.warnings,
                }, { scope: data.scope || '' })
                msg.restored = true
                this.messages.push(msg)
            }).catch(() => {
                // 会话已失效/查询失败：清除本地记录，本次不再恢复
                sessionStorage.removeItem(SESSION_KEY)
            })
        },
        // 判断页签行数据里是否含出口/进口侧内容
        sideFlags(rows, scope) {
            const list = rows || []
            const dataExport = list.some(row => row && (this.hasVal(row.hsCode) || this.hasVal(row.cnElement) ||
                this.hasVal(row.exportSource) || this.hasVal(row.dualUseRisk)))
            const dataImport = list.some(row => row && (this.hasVal(row.importHsCode) || this.hasVal(row.engName) ||
                this.hasVal(row.importElement) || this.hasVal(row.importSource)))
            // 指定范围时对应侧必展示；另一侧只有确实返回了数据才展示，避免整列都是'-'
            if (scope === '只出口') return { export: true, import: dataImport }
            if (scope === '只进口') return { export: dataExport, import: true }
            // 进出口或文字模式：按实际返回的数据决定，都没有时默认展示出口侧
            return { export: dataExport || !dataImport, import: dataImport }
        },
        // 是否有有效值
        hasVal(value) {
            return value !== null && value !== undefined && String(value).trim() !== ''
        },
        // 把接口返回的 tickets 转成页面用的页签数据
        buildTickets(res) {
            const list = (res && res.tickets) || []
            return list.map((ticket, index) => ({
                // 页签唯一标识：票号可能重复，用下标更稳
                key: 'tab-' + index,
                ticket: (ticket && ticket.ticket) || String(index + 1),
                sheetName: (ticket && ticket.sheetName) || ('页签' + (index + 1)),
                rows: this.normalizeRows(ticket && ticket.rows),
            }))
        },
        // 行数据归一化：新接口直接返回扁平字段；兼容旧的 { input, result } 形态
        normalizeRows(list) {
            return (list || []).map(item => {
                if (item && item.result) {
                    const input = item.input || {}
                    return Object.assign({}, item.result, {
                        rowNo: item.result.rowNo === null || item.result.rowNo === undefined ? input.rowNo : item.result.rowNo,
                        name: item.result.name || input.name,
                    })
                }
                return Object.assign({}, item)
            })
        },
        // 页签标题：页签名 + 行数
        tabLabel(ticket) {
            const count = (ticket.rows && ticket.rows.length) || 0
            return count ? `${ticket.sheetName}（${count} 行）` : ticket.sheetName
        },
        // 结果说明文案：Excel 按票统计，文字按行统计，恢复会话单独说明
        resultIntro(msg) {
            const country = msg.country || ''
            if (msg.restored) {
                return `已恢复上次会话（共 ${msg.totalRows} 行商品，目的国「${country}」）的申报建议，可继续补要素：`
            }
            if (msg.mode === 'text') {
                return `已根据文字描述生成 ${msg.totalRows} 行商品的申报建议（目的国「${country}」）：`
            }
            const parts = [`已解析Excel（${msg.totalTickets} 个页签，共 ${msg.totalRows} 行商品）`]
            if (msg.scope) parts.push(`推算范围「${msg.scope}」`)
            parts.push(`目的国「${country}」`)
            if (msg.save) parts.push('保存「是」')
            return parts.join('，') + '的申报建议：'
        },
        // 接口级提示文案
        warningsText(list) {
            return list && list.length ? list.join('；') : ''
        },
        // 批次号归一化：兼容接口返回数组 / 单值 / 空值
        toTextArray(value) {
            if (!value) return []
            const list = Array.isArray(value) ? value : [value]
            return list.filter(item => item !== null && item !== undefined && item !== '')
        },
        // 替换loading消息为结果
        replaceMsg(oldMsg, newMsg) {
            const idx = this.messages.indexOf(oldMsg)
            if (idx > -1) {
                this.messages.splice(idx, 1, newMsg)
            }
        },
        // 滚动到底部
        scrollBottom() {
            this.$nextTick(() => {
                const el = this.$refs.msgBody
                if (el) el.scrollTop = el.scrollHeight
            })
        },
        // 缺申报要素的行标黄
        rowClassName({ row }) {
            return row && row.elementMissing ? 'gw-row-warn' : ''
        },
        // 取嵌套属性，空值显示'-'
        getVal(obj, key) {
            const v = obj && obj[key]
            return v === null || v === undefined || v === '' ? '-' : v
        },
        // 平铺表格长文本：空值显示'-'，保留换行
        cellText(row, key) {
            const v = row && row[key]
            return v === null || v === undefined || v === '' ? '-' : String(v)
        },
        // 行级风险提示：多条用「；」拼接，空值显示'-'
        warnText(row) {
            const list = (row && row.warnings) || []
            return list.length ? list.join('；') : '-'
        },
        // el-table formatter，空值显示'-'
        fmt(row, column, cellValue) {
            return cellValue === null || cellValue === undefined || cellValue === '' ? '-' : cellValue
        },
        // 导出当前消息的全部页签：一个页签一个 sheet
        async exportExcel(msg) {
            const tickets = (msg && msg.tickets) || []
            const total = tickets.reduce((n, t) => n + ((t.rows && t.rows.length) || 0), 0)
            if (total === 0) {
                this.$message.warning('当前没有可导出的数据')
                return
            }
            if (this.exporting) return
            this.exporting = true
            try {
                const workbook = new ExcelJS.Workbook()
                const usedNames = {}
                tickets.forEach((ticket, index) => {
                    const rows = ticket.rows || []
                    if (rows.length === 0) return
                    const worksheet = workbook.addWorksheet(this.safeSheetName(ticket.sheetName, index, usedNames))
                    // 列随推算范围裁剪：只出口不导出进口列，反之亦然
                    this.fillSheet(worksheet, rows, {
                        showExport: ticket.showExport !== false,
                        showImport: ticket.showImport !== false,
                        save: !!msg.save,
                    })
                })
                const buffer = await workbook.xlsx.writeBuffer()
                const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = this.exportFileName(msg.country)
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
            } catch (e) {
                this.$message.error('导出失败，请稍后重试')
            } finally {
                this.exporting = false
            }
        },
        // 写入单个页签：列宽、表头加粗、逐行数据
        // opts: { showExport, showImport, save } —— 只出口/只进口时裁掉另一侧的列
        fillSheet(worksheet, rows, opts) {
            const options = opts || {}
            const showExport = options.showExport !== false
            const showImport = options.showImport !== false
            const showSave = !!options.save
            // 列定义：与页面表格可见列一致，并附加展开行中的申报要素/来源备注/缺项提示/风险提示
            const columns = [
                { header: '序号', key: 'rowNo', width: 8 },
                { header: '品名', key: 'name', width: 22 },
            ]
            if (showExport) {
                columns.push(
                    { header: '出口HS编码', key: 'hsCode', width: 16 },
                    { header: '出口申报要素', key: 'cnElement', width: 40, style: { alignment: { vertical: 'middle', wrapText: true } } },
                    { header: '出口来源', key: 'exportSource', width: 12 },
                    { header: '出口来源备注', key: 'exportSourceNote', width: 24, style: { alignment: { vertical: 'middle', wrapText: true } } },
                    { header: '出口缺项提示', key: 'elementMissing', width: 28, style: { alignment: { vertical: 'middle', wrapText: true } } },
                    { header: '双用途风险', key: 'dualUseRisk', width: 12 }
                )
                if (showSave) columns.push({ header: '出口可入库', key: 'exportSavableText', width: 12 })
            }
            if (showImport) {
                columns.push(
                    { header: '进口HS编码', key: 'importHsCode', width: 16 },
                    { header: '进口英文名', key: 'engName', width: 22 },
                    { header: '进口申报要素', key: 'importElement', width: 40, style: { alignment: { vertical: 'middle', wrapText: true } } },
                    { header: '进口来源', key: 'importSource', width: 12 },
                    { header: '进口来源备注', key: 'importSourceNote', width: 24, style: { alignment: { vertical: 'middle', wrapText: true } } }
                )
                if (showSave) columns.push({ header: '进口可入库', key: 'importSavableText', width: 12 })
            }
            columns.push({ header: '风险提示', key: 'warnings', width: 30, style: { alignment: { vertical: 'middle', wrapText: true } } })
            worksheet.columns = columns
            // 表头加粗
            worksheet.getRow(1).font = { bold: true }
            // 逐行写入数据
            rows.forEach(row => {
                worksheet.addRow(this.buildExportRow(row))
            })
        },
        // sheet 名：去掉Excel非法字符、截断到31字符、重名自动加序号
        safeSheetName(name, index, used) {
            let base = String(name || '').replace(/[\\/:*?\[\]]/g, '').trim() || ('页签' + (index + 1))
            base = base.slice(0, 31)
            let result = base
            let seq = 2
            while (used[result]) {
                const suffix = '(' + seq + ')'
                result = base.slice(0, 31 - suffix.length) + suffix
                seq++
            }
            used[result] = true
            return result
        },
        // 组装导出行：字段与页面表格取值保持一致，空值同样显示'-'
        buildExportRow(row) {
            return {
                rowNo: this.getVal(row, 'rowNo'),
                name: this.getVal(row, 'name'),
                hsCode: this.getVal(row, 'hsCode'),
                cnElement: this.getVal(row, 'cnElement'),
                exportSource: this.getVal(row, 'exportSource'),
                exportSourceNote: this.getVal(row, 'exportSourceNote'),
                elementMissing: this.getVal(row, 'elementMissing'),
                dualUseRisk: this.getVal(row, 'dualUseRisk'),
                // 是否保存=是时才有对应列，值是每行能否入库
                exportSavableText: row && row.exportSavable === true ? '可入库' : '不可入库',
                importSavableText: row && row.importSavable === true ? '可入库' : '不可入库',
                importHsCode: this.getVal(row, 'importHsCode'),
                engName: this.getVal(row, 'engName'),
                importElement: this.getVal(row, 'importElement'),
                importSource: this.getVal(row, 'importSource'),
                importSourceNote: this.getVal(row, 'importSourceNote'),
                warnings: row && row.warnings && row.warnings.length ? row.warnings.join('；') : '-',
            }
        },
        // 导出文件名：贸易AI助手_申报建议_目的国_时间戳.xlsx
        exportFileName(country) {
            const now = new Date()
            const pad = n => String(n).padStart(2, '0')
            const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
            // 目的国可能含文件名非法字符，去掉后再拼接
            const safeCountry = String(country || '').replace(/[\\/:*?"<>|]/g, '')
            return `贸易AI助手_申报建议${safeCountry ? '_' + safeCountry : ''}_${timestamp}.xlsx`
        },
    },
}
</script>

<style lang="css" scoped>
.gw-ai-wrap {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 86px);
    min-height: 480px;
    background: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    overflow: hidden;
}
/* 顶部 */
.gw-ai-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #ebeef5;
    background: #fff;
}
.gw-ai-title {
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
}
.gw-ai-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #67c23a;
    margin-right: 8px;
}
.gw-ai-sub {
    font-size: 12px;
    color: #909399;
}
/* 消息区 */
.gw-ai-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    background: #f7f8fa;
}
.gw-ai-empty {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #909399;
}
.gw-ai-empty i {
    font-size: 48px;
    margin-bottom: 12px;
}
.gw-ai-empty p {
    margin: 0 0 4px;
    font-size: 14px;
}
.gw-ai-empty span {
    font-size: 12px;
}
.gw-msg-row {
    display: flex;
    align-items: flex-start;
    margin-bottom: 16px;
}
.gw-msg-user {
    flex-direction: row-reverse;
}
.gw-avatar {
    flex: none;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #67c23a;
    color: #fff;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.gw-avatar-user {
    background: #909399;
}
.gw-bubble {
    max-width: 85%;
    padding: 10px 12px;
    border-radius: 8px;
}
.gw-bubble-user {
    background: #f0f9eb;
    border: 1px solid #e1f3d8;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
}
.gw-bubble-ai {
    background: #fff;
    border: 1px solid #ebeef5;
    /* 结果区是页签+宽表格，占满头像以外的剩余宽度 */
    max-width: calc(100% - 34px);
}
.gw-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 10px;
    border: 1px solid #67c23a;
    border-radius: 999px;
    font-size: 12px;
    line-height: 20px;
    color: #529b2e;
}
/* 推算范围/保存设置：弱化样式，避免和目的国胶囊抢眼 */
.gw-pill-weak {
    border-color: #dcdfe6;
    color: #606266;
}
/* 文字描述消息：保留换行，限制最大宽度 */
.gw-user-text {
    max-width: 100%;
    font-size: 13px;
    line-height: 20px;
    color: #303133;
    white-space: pre-wrap;
    word-break: break-all;
}
.gw-ai-text {
    margin: 0 0 8px;
    font-size: 14px;
    color: #303133;
}
/* 结果标题行：左侧说明 + 右侧导出按钮 */
.gw-ai-text-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
}
.gw-ai-text-row .gw-ai-text {
    margin: 0;
}
/* 接口级提示（页签序号回退/重复等） */
.gw-ai-alert {
    margin-bottom: 8px;
}
.gw-ai-alert ::v-deep .el-alert__title {
    font-size: 12px;
    line-height: 18px;
}
/* 归档结果：批次号数组做成可换行的号码胶囊 */
.gw-archive-tip {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 8px;
    padding: 8px 12px;
    border: 1px solid #e1f3d8;
    border-radius: 4px;
    background: #f0f9eb;
}
.gw-archive-icon {
    margin-top: 2px;
    font-size: 14px;
    line-height: 1;
    color: #67c23a;
}
.gw-archive-body {
    flex: 1;
    min-width: 0;
}
.gw-archive-head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 12px;
    line-height: 20px;
}
.gw-archive-title {
    font-weight: 600;
    color: #303133;
}
.gw-archive-count {
    color: #909399;
}
.gw-archive-dup {
    color: #e6a23c;
}
.gw-archive-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
}
.gw-archive-chip {
    padding: 0 8px;
    border: 1px solid #c2e7b0;
    border-radius: 3px;
    background: #fff;
    color: #3f7d22;
    font-family: Consolas, Monaco, 'Courier New', monospace;
    font-size: 12px;
    line-height: 20px;
    white-space: nowrap;
}
.gw-archive-empty {
    margin-top: 4px;
    font-size: 12px;
    line-height: 20px;
    color: #909399;
}
/* 补要素卡片：表格下方按行分组填写缺失要素 */
.gw-missing-card {
    margin-top: 8px;
    padding: 10px 12px;
    border: 1px solid #e1f3d8;
    border-radius: 4px;
    background: #f0f9eb;
}
.gw-missing-head {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    font-weight: 600;
    color: #3f7d22;
    margin-bottom: 8px;
}
.gw-missing-row {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-bottom: 8px;
    font-size: 12px;
    line-height: 28px;
    color: #606266;
    flex-wrap: wrap;
}
.gw-missing-label {
    white-space: nowrap;
}
.gw-missing-fields {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.gw-missing-field {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.gw-missing-field label {
    white-space: nowrap;
    color: #3f7d22;
    font-weight: 600;
}
.gw-missing-field ::v-deep .el-input {
    width: 140px;
}
.gw-missing-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 4px;
}
.gw-missing-tip {
    font-size: 12px;
    color: #909399;
}
/* 结果页签 */
.gw-ai-tabs ::v-deep .el-tabs__header {
    margin-bottom: 8px;
}
.gw-ai-tabs ::v-deep .el-tabs__item {
    height: 32px;
    line-height: 32px;
    font-size: 12px;
}
.gw-ai-loading,
.gw-ai-error {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: #303133;
}
.gw-ai-loading i {
    color: #67c23a;
}
.gw-ai-error,
.gw-ai-error i {
    color: #f56c6c;
}
/* AI表格：取消展开行，所有字段平铺成列，宽度超出时表格内部横向滚动 */
.gw-ai-table {
    width: 100%;
}
.gw-ai-table ::v-deep .gw-row-warn {
    background: #fdf6ec;
}
/* 长文本单元格：保留换行，最多显示3行，其余裁剪，悬停看 title 全文 */
.gw-cell-long {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: pre-line;
    word-break: break-word;
    font-size: 12px;
    line-height: 18px;
    cursor: default;
}
.gw-cell-warn {
    color: #e6a23c;
}
/* 保存=是时每行的出口/进口入库条件 */
.gw-save-flags {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}
.gw-save-flag {
    padding: 0 4px;
    border-radius: 3px;
    font-size: 11px;
    line-height: 16px;
    white-space: nowrap;
}
.gw-save-yes {
    background: #f0f9eb;
    color: #67c23a;
}
.gw-save-no {
    background: #f4f4f5;
    color: #909399;
}
/* 底部操作区：第一行解析设置，第二行文字输入 */
.gw-ai-footer {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 16px 14px;
    border-top: 1px solid #ebeef5;
    background: #fff;
}
.gw-compose-toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}
/* 目的国与解析设置之间的分隔线 */
.gw-tool-divider {
    width: 1px;
    height: 18px;
    margin: 0 2px;
    background: #e4e7ed;
}
/* 上传Excel：虚线按钮，弱化于发送按钮 */
.gw-upload-btn {
    border-style: dashed;
    color: #606266;
}
.gw-upload-btn:hover:not(.is-disabled),
.gw-upload-btn:focus:not(.is-disabled) {
    border-color: #67c23a;
    color: #67c23a;
    background: #f0f9eb;
}
.gw-compose-hint {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #c0c4cc;
}
.gw-country-select {
    width: 210px;
}
/* 是否保存 / 推算范围：标签+下拉 */
.gw-tool-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.gw-tool-label {
    font-size: 12px;
    color: #606266;
    white-space: nowrap;
}
.gw-mini-select {
    width: 84px;
}
.gw-scope-select {
    width: 100px;
}
/* 文字输入：整体一个圆角输入框，聚焦时绿色描边，发送按钮在框内右下 */
.gw-compose-box {
    position: relative;
    padding: 8px 12px;
    border: 1px solid #dcdfe6;
    border-radius: 8px;
    background: #fff;
    transition: border-color 0.2s, box-shadow 0.2s;
}
.gw-compose-box.gw-compose-focus {
    border-color: #67c23a;
    box-shadow: 0 0 0 2px rgba(103, 194, 58, 0.12);
}
.gw-text-input ::v-deep .el-textarea__inner {
    padding: 0;
    border: none;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    font-size: 13px;
    line-height: 20px;
    resize: none;
}
.gw-text-input ::v-deep .el-textarea__inner:focus {
    box-shadow: none;
}
.gw-text-input ::v-deep .el-textarea__inner::placeholder {
    color: #c0c4cc;
}
.gw-compose-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 6px;
}
.gw-compose-kbd {
    font-size: 12px;
    color: #c0c4cc;
    user-select: none;
}
.gw-opt-code {
    margin-right: 16px;
    color: #909399;
}
.gw-opt-name {
    margin-right: 16px;
}
.gw-file-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border: 1px solid #dcdfe6;
    border-radius: 999px;
    background: #f4f4f5;
    font-size: 12px;
    color: #606266;
    max-width: 260px;
}
.gw-file-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.gw-file-chip .el-icon-close {
    cursor: pointer;
    color: #909399;
}
.gw-file-chip .el-icon-close:hover {
    color: #f56c6c;
}
</style>
